// src/plugins/autotrack/modules/PageViewModule.ts

import { BaseTrackingModule } from './BaseTrackingModule';
import { PageViewConfig } from '../types';
import { AutoTrackUtils } from '../utils';
import { IUniqodeInsightsClient } from '@/types';
import { ILogger } from '@/utils/logger';

export class PageViewModule extends BaseTrackingModule {
  private config: Required<PageViewConfig>;
  private lastUrl: string = '';
  private pageLoadTime: number = 0;

  constructor(config: PageViewConfig = {}) {
    super('PageView');

    this.config = {
      enabled: true,
      trackHashChanges: true,
      trackSearchParams: true,
      captureReferrer: true,
      captureUserAgent: false,
      captureScreenResolution: true,
      ...config,
    };
  }

  protected setupTracking(): void {
    this.logger?.debug('PageViewModule: Initializing page view tracking', this.config);
    if (!this.config.enabled) {
      this.logger?.debug('PageViewModule: Tracking is disabled');
      return;
    }
    this.logger?.debug('PageViewModule: Setting up page view tracking');

    this.pageLoadTime = Date.now();
    this.lastUrl = window.location.href;

    // Track initial page view
    this.trackPageView();

    // Track hash changes if enabled
    if (this.config.trackHashChanges) {
      this.addEventListener(window, 'hashchange', () => {
        this.handleUrlChange();
      });
    }

    // Track popstate (back/forward navigation)
    this.addEventListener(window, 'popstate', () => {
      this.handleUrlChange();
    });

    // Track pushstate/replacestate (SPA navigation)
    this.interceptHistoryMethods();
  }

  protected cleanup(): void {
    // Restore original history methods if they were intercepted
    this.restoreHistoryMethods();
  }

  private trackPageView(): void {
    const viewport = AutoTrackUtils.getViewportDimensions();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pageData: Record<string, any> = {
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      search: this.config.trackSearchParams ? window.location.search : undefined,
      hash: this.config.trackHashChanges ? window.location.hash : undefined,
      viewport_width: viewport.width,
      viewport_height: viewport.height,
    };

    if (this.config.captureReferrer) {
      pageData.referrer = document.referrer || undefined;
    }

    if (this.config.captureUserAgent) {
      pageData.user_agent = navigator.userAgent;
    }

    if (this.config.captureScreenResolution) {
      pageData.screen_width = screen.width;
      pageData.screen_height = screen.height;
      pageData.screen_color_depth = screen.colorDepth;
    }

    // Add page load performance data
    if (performance?.timing) {
      const { timing } = performance;
      pageData.page_load_time = timing.loadEventEnd - timing.navigationStart;
      pageData.dom_ready_time = timing.domContentLoadedEventEnd - timing.navigationStart;
    }
    this.logger?.debug('Tracking page view:', pageData);
    this.trackEvent({
      eventType: '$pageview',
      properties: pageData,
    });
  }

  private handleUrlChange(): void {
    const currentUrl = window.location.href;

    if (currentUrl !== this.lastUrl) {
      this.lastUrl = currentUrl;
      this.pageLoadTime = Date.now();
      this.trackPageView();
    }
  }

  private originalPushState?: typeof history.pushState;
  private originalReplaceState?: typeof history.replaceState;

  private interceptHistoryMethods(): void {
    // Store original methods
    this.originalPushState = history.pushState;
    this.originalReplaceState = history.replaceState;

    // Override pushState
    history.pushState = (...args) => {
      this.originalPushState?.apply(history, args);
      setTimeout(() => this.handleUrlChange(), 0);
    };

    // Override replaceState
    history.replaceState = (...args) => {
      this.originalReplaceState?.apply(history, args);
      setTimeout(() => this.handleUrlChange(), 0);
    };
  }

  private restoreHistoryMethods(): void {
    if (this.originalPushState) {
      history.pushState = this.originalPushState;
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
    }
  }

  /**
   * Get current page session data
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getPageSessionData(): Record<string, any> {
    const timeOnPage = Date.now() - this.pageLoadTime;

    return {
      time_on_page: timeOnPage,
      page_load_time: this.pageLoadTime,
      current_url: window.location.href,
      scroll_percentage: AutoTrackUtils.getScrollPercentage(),
    };
  }

  /**
   * Manually track a page view (useful for SPA route changes)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public trackManualPageView(additionalData: Record<string, any> = {}): void {
    if (!this.isActive) {
      return;
    }

    this.pageLoadTime = Date.now();
    this.lastUrl = window.location.href;

    // Merge additional data with standard page view data
    const pageData = {
      ...this.getStandardPageData(),
      ...additionalData,
      manual_tracking: true,
    };

    this.trackEvent({
      eventType: '$pageview',
      properties: pageData,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getStandardPageData(): Record<string, any> {
    const viewport = AutoTrackUtils.getViewportDimensions();

    return {
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      search: this.config.trackSearchParams ? window.location.search : undefined,
      hash: this.config.trackHashChanges ? window.location.hash : undefined,
      viewport_width: viewport.width,
      viewport_height: viewport.height,
      referrer: this.config.captureReferrer ? document.referrer : undefined,
    };
  }
}
